import React, { useEffect, useState } from "react";
import { Send, Users, Store, ShoppingBag, Search, X } from "lucide-react";
import { toast } from "sonner";
import client, { apiErr } from "@/lib/api";

const MODES = [
  { key: "all", label: "إشعار عام", sub: "كل المستخدمين", icon: Users, color: "var(--k-ink)" },
  { key: "merchants", label: "إلى التجار", sub: "كل التجار", icon: Store, color: "var(--k-gold-deep)" },
  { key: "buyers", label: "إلى المشترين", sub: "كل المشترين", icon: ShoppingBag, color: "var(--k-emerald)" },
];

export default function AdminNotifications() {
  const [mode, setMode] = useState("all");        // all | merchants | buyers
  const [scope, setScope] = useState("group");     // group | one
  const [msg, setMsg] = useState({ title: "", body: "" });
  const [sending, setSending] = useState(false);
  // targeted-to-one
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);

  const roleForSearch = mode === "merchants" ? "merchant" : mode === "buyers" ? "buyer" : "all";

  useEffect(() => {
    if (scope !== "one") return;
    const t = setTimeout(() => {
      client.get("/admin/users", { params: { q: q || undefined, role: roleForSearch, status: "all" } })
        .then((r) => setResults(r.data.slice(0, 12))).catch(() => setResults([]));
    }, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [q, scope, mode]);

  useEffect(() => { setPicked(null); setResults([]); setQ(""); }, [mode, scope]);

  const send = async () => {
    if (!msg.title.trim() || !msg.body.trim()) { toast.error("العنوان والنص مطلوبان"); return; }
    const payload = scope === "one"
      ? { target: "user", userId: picked?.id, title: msg.title, body: msg.body }
      : { target: mode, title: msg.title, body: msg.body };
    if (scope === "one" && !picked) { toast.error("اختر مستخدماً محدداً"); return; }
    setSending(true);
    try {
      const r = await client.post("/admin/notify", payload);
      toast.success(`تم إرسال الإشعار إلى ${r.data.count} مستخدم`);
      setMsg({ title: "", body: "" }); setPicked(null); setQ("");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div dir="rtl">
      <h2 className="font-head text-[16px] font-bold px-4 pt-5 pb-2 flex items-center gap-1.5"><Send size={17} style={{ color: "var(--k-gold-deep)" }} /> الإشعارات</h2>
      <p className="text-[12px] px-4 mb-2" style={{ color: "var(--k-ink-soft)" }}>اختر نوع الإشعار: عام لكل المستخدمين، أو مخصّص لفئة (التجار / المشترين)، أو لمستخدم محدد.</p>

      {/* Mode segmented */}
      <div className="px-4 grid grid-cols-3 gap-2 mb-3">
        {MODES.map((m) => {
          const Icon = m.icon; const on = mode === m.key;
          return (
            <button key={m.key} data-testid={`notify-mode-${m.key}`} onClick={() => setMode(m.key)} className="k-tap rounded-2xl p-3 flex flex-col items-center gap-1 text-center transition-all" style={{ background: on ? m.color : "var(--k-surface)", color: on ? "#fff" : "var(--k-ink)", border: `1px solid ${on ? m.color : "var(--k-line)"}` }}>
              <Icon size={18} style={{ color: on ? "#fff" : m.color }} />
              <span className="text-[12px] font-bold">{m.label}</span>
              <span className="text-[10px]" style={{ color: on ? "rgba(255,255,255,.7)" : "var(--k-ink-soft)" }}>{m.sub}</span>
            </button>
          );
        })}
      </div>

      {/* Scope: group vs one */}
      <div className="px-4 flex gap-2 mb-3">
        <button data-testid="notify-scope-group" onClick={() => setScope("group")} className="k-tap flex-1 py-2 rounded-lg text-[12px] font-bold" style={{ background: scope === "group" ? "var(--k-gold-tint)" : "var(--k-surface)", color: scope === "group" ? "var(--k-gold-deep)" : "var(--k-ink-soft)", border: "1px solid var(--k-line)" }}>{mode === "all" ? "كل المستخدمين" : mode === "merchants" ? "كل التجار" : "كل المشترين"}</button>
        <button data-testid="notify-scope-one" onClick={() => setScope("one")} className="k-tap flex-1 py-2 rounded-lg text-[12px] font-bold" style={{ background: scope === "one" ? "var(--k-gold-tint)" : "var(--k-surface)", color: scope === "one" ? "var(--k-gold-deep)" : "var(--k-ink-soft)", border: "1px solid var(--k-line)" }} disabled={mode === "all" ? false : false}>مستخدم محدّد</button>
      </div>

      {/* Specific user picker */}
      {scope === "one" && (
        <div className="px-4 mb-3">
          {picked ? (
            <div className="flex items-center justify-between gap-2 rounded-lg px-3 py-2.5" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
              <span className="text-[13px] font-bold">{picked.name} <span className="font-num text-[11px]" style={{ color: "var(--k-ink-soft)" }}>· {picked.email}</span></span>
              <button data-testid="notify-clear-pick" onClick={() => setPicked(null)} className="k-tap"><X size={15} style={{ color: "var(--k-ruby)" }} /></button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 rounded-lg px-3 mb-1" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
                <Search size={15} style={{ color: "var(--k-ink-soft)" }} />
                <input data-testid="notify-user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث عن مستخدم بالاسم أو البريد" className="flex-1 bg-transparent py-2.5 text-[13px] outline-none" />
              </div>
              {results.length > 0 && (
                <div className="rounded-lg overflow-hidden" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
                  {results.map((u) => (
                    <button key={u.id} data-testid={`notify-pick-${u.id}`} onClick={() => setPicked(u)} className="k-tap w-full text-right px-3 py-2 text-[12.5px]" style={{ borderBottom: "1px solid var(--k-line)" }}>
                      {u.name} <span className="font-num text-[10.5px]" style={{ color: "var(--k-ink-soft)" }}>· {u.email} · {u.role === "merchant" ? "تاجر" : u.role === "admin" ? "مدير" : "مشترٍ"}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Message */}
      <div className="rounded-2xl mx-4 p-4 mb-4" style={{ background: "var(--k-ink)", color: "#fff" }}>
        <input data-testid="notify-title" value={msg.title} onChange={(e) => setMsg((m) => ({ ...m, title: e.target.value }))} placeholder="العنوان" className="w-full rounded-lg px-3 py-2.5 text-[13px] mb-2" style={{ background: "rgba(255,255,255,.12)", color: "#fff", border: "1px solid rgba(255,255,255,.15)" }} />
        <textarea data-testid="notify-body" value={msg.body} onChange={(e) => setMsg((m) => ({ ...m, body: e.target.value }))} placeholder="نص الرسالة" rows={3} className="w-full rounded-lg px-3 py-2.5 text-[13px] mb-3" style={{ background: "rgba(255,255,255,.12)", color: "#fff", border: "1px solid rgba(255,255,255,.15)" }} />
        <button data-testid="notify-send" onClick={send} disabled={sending} className="k-tap w-full py-2.5 rounded-lg font-bold text-[13px] flex items-center justify-center gap-1.5" style={{ background: "var(--k-gold)", color: "var(--k-ink)" }}><Send size={15} /> {sending ? "جارٍ الإرسال…" : "إرسال الإشعار"}</button>
      </div>
    </div>
  );
}
